import { useMemo, useEffect, FC, createContext } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { useAppSelector } from "../../states/hooks";
import { selectThemeMode } from "../../states/appState/selectors";
import { saveSystemTheme } from "../utils/functions/systemTheme";

import CssBaseline from "@mui/material/CssBaseline";

export interface ThemeContextType {
    themeMode: "dark" | "light";
}

export const ThemeContext = createContext<ThemeContextType>({
    themeMode: "dark",
});

export const ThemeContextProvider: FC = ({ children }) => {
    const themeMode = useAppSelector(selectThemeMode);
    if (themeMode) saveSystemTheme(themeMode);

    const isLight = themeMode === "light";

    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        html.setAttribute("data-sm-theme", themeMode);
        html.setAttribute("data-mui-color-scheme", themeMode);
        body.setAttribute("data-sm-theme", themeMode);
        body.classList.toggle("sm-theme-light", themeMode === "light");
        body.classList.toggle("sm-theme-dark", themeMode !== "light");
    }, [themeMode]);

    const theme = useMemo(() => {
        return createTheme({
            palette: {
                mode: themeMode,
                primary: {
                    main: "#00d2ff",
                    light: "#66ecff",
                    dark: "#0682a7",
                    contrastText: "#031625",
                },
                secondary: {
                    main: "#37f4c8",
                    light: "#8fffe3",
                    dark: "#0aa483",
                    contrastText: "#031625",
                },
                background: {
                    default: isLight ? "#f2f8fc" : "#06111f",
                    paper: isLight ? "#ffffff" : "#0b182a",
                },
                text: {
                    primary: isLight ? "#0c1b2a" : "#edf6ff",
                    secondary: isLight ? "#334b60" : "#a8c0d7",
                },
                divider: isLight
                    ? "rgba(20,48,72,.13)"
                    : "rgba(180,214,244,.16)",
            },
            shape: {
                borderRadius: 18,
            },
            typography: {
                fontFamily:
                    'Arial, Helvetica, system-ui, -apple-system, "Segoe UI", sans-serif',
                button: {
                    textTransform: "none",
                    fontWeight: 900,
                    letterSpacing: 0,
                },
                h1: { fontWeight: 950, letterSpacing: "-0.035em" },
                h2: { fontWeight: 950, letterSpacing: "-0.035em" },
                h3: { fontWeight: 950, letterSpacing: "-0.035em" },
                h4: { fontWeight: 900, letterSpacing: "-0.025em" },
                h5: { fontWeight: 900, letterSpacing: "-0.018em" },
                h6: { fontWeight: 850, letterSpacing: "-0.012em" },
            },
            components: {
                MuiCssBaseline: {
                    styleOverrides: {
                        body: {
                            fontFamily:
                                'Arial, Helvetica, system-ui, -apple-system, "Segoe UI", sans-serif',
                            background: isLight ? "#f2f8fc" : "#06111f",
                        },
                    },
                },
                MuiButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 999,
                            textTransform: "none",
                            fontWeight: 900,
                        },
                    },
                },
                MuiIconButton: {
                    styleOverrides: {
                        root: {
                            borderRadius: 16,
                        },
                    },
                },
                MuiPaper: {
                    styleOverrides: {
                        root: {
                            backgroundImage: "none",
                        },
                    },
                },
                MuiTableContainer: {
                    styleOverrides: {
                        root: {
                            borderRadius: 24,
                        },
                    },
                },
                MuiTableCell: {
                    styleOverrides: {
                        root: {
                            fontWeight: 700,
                        },
                        head: {
                            fontWeight: 950,
                            textTransform: "uppercase",
                            letterSpacing: ".08em",
                            fontSize: 12,
                        },
                    },
                },
                MuiDrawer: {
                    styleOverrides: {
                        paper: {
                            borderRadius: "0 0 28px 28px",
                        },
                    },
                },
            },
        });
    }, [themeMode, isLight]);

    return (
        <ThemeContext.Provider value={{ themeMode }}>
            <ThemeProvider theme={theme}>
                <CssBaseline enableColorScheme />
                {children}
            </ThemeProvider>
        </ThemeContext.Provider>
    );
};
